import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Switch } from "./ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  Megaphone, Users, Trophy, Layers, DollarSign, Target, TrendingUp, Plus, Pencil,
  Trash2, Loader2, Clock, KeyRound, UserPlus, FileSpreadsheet, Rocket, Check, Building2, LifeBuoy,
} from "lucide-react";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";
import { STATUS_META, LEAD_STATUSES, money, fmtDate, PLAN_OPTIONS } from "../lib/marketing";
import { AdminOrgsDialog } from "./AdminOrgsDialog";

const KpiBig = ({ icon: Icon, label, value, tone, testid }) => (
  <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2 text-xs font-semibold text-slate-500">
      <Icon className={`h-4 w-4 text-${tone}-600`} /> {label}
    </div>
    <div className="mt-1.5 text-2xl font-extrabold text-slate-900">{value}</div>
  </div>
);

const emptyM = { name: "", username: "", password: "", phone: "", monthly_target: "", commission_rate: "" };

function MarketerForm({ open, onOpenChange, marketer, onSaved }) {
  const editing = Boolean(marketer?.id);
  const [f, setF] = useState(emptyM);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(marketer?.id ? {
      name: marketer.name || "", username: marketer.username || "", password: "",
      phone: marketer.phone || "", monthly_target: marketer.monthly_target || "", commission_rate: marketer.commission_rate || "",
    } : emptyM);
  }, [open, marketer]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim() || !f.username.trim()) { toast.warning("الاسم واسم المستخدم مطلوبان"); return; }
    if (!editing && !/^\d{6}$/.test(f.password.trim())) { toast.warning("الرمز السري يجب أن يكون 6 أرقام بالضبط"); return; }
    setBusy(true);
    try {
      const payload = {
        name: f.name, username: f.username, phone: f.phone,
        monthly_target: parseFloat(f.monthly_target) || 0, commission_rate: parseFloat(f.commission_rate) || 0,
      };
      if (editing) { if (f.password.trim()) payload.password = f.password.trim(); await api.adminUpdateMarketer(marketer.id, payload); }
      else await api.adminCreateMarketer({ ...payload, password: f.password.trim() });
      toast.success(editing ? "تم التحديث" : "تم إنشاء المسوّق");
      onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body sm:max-w-md" data-testid="marketer-form">
        <DialogHeader className="text-start"><DialogTitle className="font-heading text-xl">{editing ? "تعديل مسوّق" : "إضافة مسوّق"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label>الاسم *</Label><Input data-testid="m-name" value={f.name} onChange={set("name")} /></div>
          <div className="space-y-1"><Label>اسم المستخدم *</Label><Input data-testid="m-username" dir="ltr" value={f.username} onChange={set("username")} disabled={editing} /></div>
          <div className="space-y-1"><Label>{editing ? "كلمة مرور جديدة" : "كلمة المرور *"}</Label><Input data-testid="m-password" type="text" dir="ltr" placeholder={editing ? "اتركها فارغة" : ""} value={f.password} onChange={set("password")} /></div>
          <div className="space-y-1"><Label>الجوال</Label><Input dir="ltr" value={f.phone} onChange={set("phone")} /></div>
          <div className="space-y-1"><Label>الهدف الشهري (عدد)</Label><Input type="number" dir="ltr" value={f.monthly_target} onChange={set("monthly_target")} /></div>
          <div className="col-span-2 space-y-1"><Label>نسبة العمولة % (من القيمة المحققة)</Label><Input type="number" dir="ltr" value={f.commission_rate} onChange={set("commission_rate")} /></div>
        </div>
        <DialogFooter><Button disabled={busy} onClick={save} data-testid="m-save" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} حفظ</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MarketingAdminPanel({ open, onOpenChange, initialTab = "overview" }) {
  const [ov, setOv] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [formOpen, setFormOpen] = useState(false);
  const [editM, setEditM] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadMarketer, setLeadMarketer] = useState("all");
  const [leadStatus, setLeadStatus] = useState("all");
  const [comm, setComm] = useState(null);
  const [commMonth, setCommMonth] = useState(new Date().toISOString().slice(0, 7));
  const [cred, setCred] = useState(null);
  const [actLead, setActLead] = useState(null);
  const [actPlan, setActPlan] = useState("business");
  const [actMonths, setActMonths] = useState("1");
  const [actPhone, setActPhone] = useState("");
  const [actBusy, setActBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, m] = await Promise.all([api.adminMarketingOverview(), api.adminMetrics().catch(() => null)]);
      setOv(o); setMetrics(m);
    } catch (e) { toast.error("تعذّر تحميل البيانات"); }
    finally { setLoading(false); }
  }, []);
  const loadLeads = useCallback(async () => {
    try { const d = await api.adminMarketingLeads(leadMarketer === "all" ? null : leadMarketer, leadStatus === "all" ? null : leadStatus); setLeads(d.leads || []); }
    catch (e) { /* */ }
  }, [leadMarketer, leadStatus]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => { if (open) setTab(initialTab); }, [open, initialTab]);
  useEffect(() => { if (open && tab === "leads") loadLeads(); }, [open, tab, loadLeads]);
  const loadComm = useCallback(async () => {
    try { setComm(await api.adminMarketingCommissions(commMonth)); } catch (e) { /* */ }
  }, [commMonth]);
  useEffect(() => { if (open && tab === "commissions") loadComm(); }, [open, tab, loadComm]);
  const exportComm = async () => {
    try {
      const blob = await api.adminExportCommissions(commMonth);
      await saveBlob(blob, `commissions_${commMonth}.xlsx`);
    } catch (e) { toast.error("تعذّر التصدير"); }
  };

  const toggleActive = async (m) => {
    try { await api.adminUpdateMarketer(m.id, { active: !m.active }); load(); }
    catch (e) { toast.error("خطأ"); }
  };
  const del = async (m) => {
    if (!window.confirm(`حذف المسوّق ${m.name}؟`)) return;
    try { await api.adminDeleteMarketer(m.id); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error("تعذّر الحذف"); }
  };
  const openActivate = (l) => {
    setActLead(l);
    setActPlan(PLAN_OPTIONS.some((p) => p.v === l.expected_plan) ? l.expected_plan : "business");
    setActPhone(l.phone || "");
    setActMonths("1");
  };
  const doActivate = async () => {
    if (!actLead) return;
    setActBusy(true);
    try {
      const r = await api.adminActivateLead(actLead.id, { plan: actPlan, months: parseInt(actMonths, 10) || 1, phone: actPhone });
      setActLead(null);
      setCred({ company: actLead.company_name, ...r });
      toast.success("تم تفعيل الاشتراك وإنشاء حساب الشركة");
      loadLeads(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر التفعيل"); }
    finally { setActBusy(false); }
  };
  const markPaid = async (r, paid) => {
    try { await api.adminPayCommission(r.id, commMonth, paid); loadComm(); toast.success(paid ? "تم تعليمها كمدفوعة" : "أُلغيت حالة الدفع"); }
    catch (e) { toast.error("خطأ"); }
  };

  const t = ov?.totals || {};
  const funnel = ov?.funnel || {};
  const maxFunnel = Math.max(1, ...LEAD_STATUSES.map((s) => funnel[s] || 0));
  const medals = ["🥇", "🥈", "🥉"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="font-body max-w-5xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="marketing-admin-panel">
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Megaphone className="h-6 w-6 text-emerald-600" /> منصة التسويق — متابعة الأداء</DialogTitle>
        </DialogHeader>

        {loading ? <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div> : (
          <Tabs value={tab} onValueChange={setTab} dir="rtl">
            <TabsList className="flex flex-wrap h-auto gap-1 w-full bg-slate-100">
              <TabsTrigger value="overview" data-testid="tab-overview" className="text-xs sm:text-sm">نظرة عامة</TabsTrigger>
              <TabsTrigger value="companies" data-testid="tab-companies" className="gap-1 text-xs sm:text-sm"><Building2 className="h-3.5 w-3.5" /> الشركات والاشتراكات</TabsTrigger>
              <TabsTrigger value="support" data-testid="tab-support" className="gap-1 text-xs sm:text-sm"><LifeBuoy className="h-3.5 w-3.5" /> الدعم الفني</TabsTrigger>
              <TabsTrigger value="marketers" data-testid="tab-marketers" className="text-xs sm:text-sm">المسوّقون</TabsTrigger>
              <TabsTrigger value="leads" data-testid="tab-leads" className="text-xs sm:text-sm">العملاء</TabsTrigger>
              <TabsTrigger value="commissions" data-testid="tab-commissions" className="text-xs sm:text-sm">العمولات</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-5 pt-4">
              {metrics && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-slate-500 flex items-center gap-1.5"><Building2 className="h-4 w-4 text-blue-600" /> الشركات والاشتراكات</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <KpiBig testid="ov-companies" icon={Building2} label="إجمالي الشركات" value={metrics.total_companies} tone="blue" />
                    <KpiBig testid="ov-active-companies" icon={Check} label="شركات مشتركة" value={metrics.active_companies} tone="emerald" />
                    <KpiBig testid="ov-monthly-rev" icon={DollarSign} label="الإيراد الشهري" value={money(metrics.monthly_revenue)} tone="emerald" />
                    <KpiBig testid="ov-annual-rev" icon={TrendingUp} label="الإيراد السنوي" value={money(metrics.annual_revenue)} tone="indigo" />
                  </div>
                </div>
              )}
              <div className="text-xs font-bold text-slate-500 flex items-center gap-1.5"><Megaphone className="h-4 w-4 text-emerald-600" /> فريق التسويق</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiBig testid="ov-marketers" icon={Users} label="المسوّقون" value={`${t.active}/${t.marketers}`} tone="blue" />
                <KpiBig testid="ov-won" icon={Trophy} label="اشتراكات محقّقة" value={t.won} tone="emerald" />
                <KpiBig testid="ov-inprogress" icon={Layers} label="تحت الإجراء" value={t.in_progress} tone="amber" />
                <KpiBig testid="ov-wonvalue" icon={DollarSign} label="قيمة محقّقة/شهر" value={money(t.won_value)} tone="emerald" />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="font-bold text-slate-800 mb-3 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600" /> قمع المبيعات</div>
                  <div className="space-y-2">
                    {LEAD_STATUSES.map((s) => (
                      <div key={s} className="flex items-center gap-2">
                        <span className="w-20 text-xs text-slate-600 shrink-0">{STATUS_META[s].label}</span>
                        <div className="flex-1 h-5 rounded-full bg-slate-100 overflow-hidden">
                          <div className={`h-full ${STATUS_META[s].dot}`} style={{ width: `${((funnel[s] || 0) / maxFunnel) * 100}%` }} />
                        </div>
                        <span className="w-8 text-xs font-bold text-slate-700 text-left">{funnel[s] || 0}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="font-bold text-slate-800 mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> لوحة الصدارة</div>
                  <div className="space-y-2">
                    {(ov?.leaderboard || []).slice(0, 5).map((m, i) => (
                      <div key={m.id} data-testid={`leader-${i}`} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2">
                        <span className="flex items-center gap-2 text-sm font-semibold text-slate-800"><span className="w-5 text-center">{medals[i] || i + 1}</span> {m.name}</span>
                        <span className="text-xs text-slate-500">{m.won} اشتراك · {money(m.won_value)}</span>
                      </div>
                    ))}
                    {(ov?.leaderboard || []).length === 0 && <p className="text-sm text-slate-400">لا مسوّقين بعد.</p>}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-100 font-bold text-slate-800">أداء كل مسوّق</div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[720px]">
                    <thead><tr className="text-xs text-slate-400 bg-slate-50">
                      <th className="text-start px-3 py-2">المسوّق</th><th className="px-3 py-2">مشترك</th><th className="px-3 py-2">تحت الإجراء</th>
                      <th className="px-3 py-2">مرفوض</th><th className="px-3 py-2">تحويل</th><th className="px-3 py-2">هدف الشهر</th><th className="px-3 py-2">قيمة محقّقة</th>
                      <th className="px-3 py-2">ساعات الشهر</th><th className="px-3 py-2">آخر نشاط</th><th className="px-3 py-2">الحالة</th>
                    </tr></thead>
                    <tbody>
                      {(ov?.marketers || []).map((m) => (
                        <tr key={m.id} data-testid={`perf-row-${m.id}`} className="border-t border-slate-100 text-center">
                          <td className="text-start px-3 py-2 font-semibold text-slate-800">{m.name}</td>
                          <td className="px-3 py-2 text-emerald-700 font-bold">{m.won}</td>
                          <td className="px-3 py-2 text-amber-700">{m.in_progress}</td>
                          <td className="px-3 py-2 text-rose-600">{m.lost}</td>
                          <td className="px-3 py-2">{m.conversion}%</td>
                          <td className="px-3 py-2 font-semibold text-slate-600">{m.won_month ?? 0}/{m.monthly_target || 0}</td>
                          <td className="px-3 py-2 font-semibold">{money(m.won_value)}</td>
                          <td className="px-3 py-2"><span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 text-slate-400" />{m.hours_month}س {m.clocked_in && <span className="h-2 w-2 rounded-full bg-emerald-500" />}</span></td>
                          <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(m.last_activity)}</td>
                          <td className="px-3 py-2">{m.active ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">نشط</Badge> : <Badge variant="outline" className="bg-slate-100 text-slate-500">موقوف</Badge>}</td>
                        </tr>
                      ))}
                      {(ov?.marketers || []).length === 0 && <tr><td colSpan={10} className="text-center py-8 text-slate-400">لا مسوّقين — أضِف أول مسوّق من تبويب «المسوّقون».</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="marketers" className="space-y-3 pt-4">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => { setEditM(null); setFormOpen(true); }} data-testid="add-marketer-btn" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"><UserPlus className="h-4 w-4" /> إضافة مسوّق</Button>
              </div>
              <div className="grid gap-2">
                {(ov?.marketers || []).map((m) => (
                  <div key={m.id} data-testid={`marketer-row-${m.id}`} className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-bold text-slate-900">{m.name} <span className="text-xs text-slate-400 font-mono">@{m.username}</span></div>
                      <div className="text-xs text-slate-500 mt-0.5">هدف: {m.monthly_target} · عمولة: {m.commission_rate}% · محقّق: {m.won} ({money(m.won_value)})</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 text-xs text-slate-500"><Switch checked={m.active} onCheckedChange={() => toggleActive(m)} data-testid={`toggle-active-${m.id}`} /> {m.active ? "نشط" : "موقوف"}</div>
                      <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => { setEditM(m); setFormOpen(true); }} data-testid={`edit-marketer-${m.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="icon" variant="outline" className="h-8 w-8 text-rose-600 border-rose-200" onClick={() => del(m)} data-testid={`del-marketer-${m.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                ))}
                {(ov?.marketers || []).length === 0 && <div className="py-12 text-center text-slate-400 border border-dashed rounded-2xl">لا مسوّقين بعد.</div>}
              </div>
            </TabsContent>

            <TabsContent value="leads" className="space-y-3 pt-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={leadMarketer} onValueChange={setLeadMarketer}>
                  <SelectTrigger className="w-44" data-testid="filter-marketer"><SelectValue placeholder="كل المسوّقين" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">كل المسوّقين</SelectItem>{(ov?.marketers || []).map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={leadStatus} onValueChange={setLeadStatus}>
                  <SelectTrigger className="w-36" data-testid="filter-lead-status"><SelectValue placeholder="كل الحالات" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">كل الحالات</SelectItem>{LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                {leads.map((l) => (
                  <div key={l.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-semibold text-slate-900">{l.company_name} <Badge variant="outline" className={`ml-1 ${STATUS_META[l.status]?.cls}`}>{STATUS_META[l.status]?.label}</Badge></div>
                      <div className="text-xs text-slate-500 mt-0.5">{l.marketer_name} · {l.city || "—"} · {money(l.monthly_value)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {l.activated_org_id ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">مُفعّل ✓</Badge>
                      ) : l.status === "won" ? (
                        <Button size="sm" onClick={() => openActivate(l)} data-testid={`activate-lead-${l.id}`} className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-700 text-xs"><Rocket className="h-3.5 w-3.5" /> تفعيل الشركة</Button>
                      ) : null}
                      <span className="text-xs text-slate-400">{fmtDate(l.updated_at)}</span>
                    </div>
                  </div>
                ))}
                {leads.length === 0 && <div className="py-12 text-center text-slate-400 border border-dashed rounded-2xl">لا عملاء مطابقون.</div>}
              </div>
            </TabsContent>
            <TabsContent value="companies" className="pt-2">
              <AdminOrgsDialog embed visibleTabs={["companies"]} initialTab="companies" open={open} onOpenChange={() => {}} />
            </TabsContent>

            <TabsContent value="support" className="pt-2">
              <AdminOrgsDialog embed visibleTabs={["support"]} initialTab="support" open={open} onOpenChange={() => {}} />
            </TabsContent>

            <TabsContent value="commissions" className="space-y-3 pt-4">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Label className="text-sm">شهر الاستحقاق</Label>
                  <Input type="month" dir="ltr" className="w-40" value={commMonth} onChange={(e) => setCommMonth(e.target.value)} data-testid="comm-month" />
                </div>
                <Button size="sm" onClick={exportComm} data-testid="comm-export" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700"><FileSpreadsheet className="h-4 w-4" /> تصدير Excel</Button>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead><tr className="text-xs text-slate-400 bg-slate-50">
                    <th className="text-start px-3 py-2">المسوّق</th><th className="px-3 py-2">عدد الاشتراكات</th>
                    <th className="px-3 py-2">القيمة المحقّقة/شهر</th><th className="px-3 py-2">نسبة العمولة</th><th className="px-3 py-2">العمولة المستحقّة</th><th className="px-3 py-2">الحالة</th>
                  </tr></thead>
                  <tbody>
                    {(comm?.rows || []).map((r) => (
                      <tr key={r.id} data-testid={`comm-row-${r.id}`} className="border-t border-slate-100 text-center">
                        <td className="text-start px-3 py-2 font-semibold text-slate-800">{r.name}</td>
                        <td className="px-3 py-2">{r.count}</td>
                        <td className="px-3 py-2">{money(r.won_value)}</td>
                        <td className="px-3 py-2">{r.commission_rate}%</td>
                        <td className="px-3 py-2 font-extrabold text-emerald-700">{money(r.commission)}</td>
                        <td className="px-3 py-2">
                          {r.paid ? (
                            <button onClick={() => markPaid(r, false)} data-testid={`comm-unpay-${r.id}`} className="inline-flex flex-col items-center gap-0.5">
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer">مدفوعة ✓</Badge>
                              <span className="text-[10px] text-slate-400">{fmtDate(r.paid_at)}</span>
                            </button>
                          ) : (
                            <Button size="sm" variant="outline" disabled={!r.commission} onClick={() => markPaid(r, true)} data-testid={`comm-pay-${r.id}`} className="h-7 text-xs gap-1 border-emerald-200 text-emerald-700"><Check className="h-3.5 w-3.5" /> تعليم كمدفوعة</Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {comm && (
                      <tr className="border-t-2 border-slate-200 bg-slate-50 text-center font-bold" data-testid="comm-totals">
                        <td className="text-start px-3 py-2">الإجمالي</td>
                        <td className="px-3 py-2">{comm.totals.count}</td>
                        <td className="px-3 py-2">{money(comm.totals.won_value)}</td>
                        <td className="px-3 py-2">—</td>
                        <td className="px-3 py-2 text-emerald-700">{money(comm.totals.commission)}</td>
                        <td className="px-3 py-2">—</td>
                      </tr>
                    )}
                    {comm && (comm.rows || []).length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">لا عمولات مستحقّة لهذا الشهر.</td></tr>}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
      <MarketerForm open={formOpen} onOpenChange={setFormOpen} marketer={editM} onSaved={load} />
      <Dialog open={!!actLead} onOpenChange={(o) => !o && setActLead(null)}>
        <DialogContent dir="rtl" className="font-body sm:max-w-md" data-testid="activate-dialog">
          <DialogHeader className="text-start"><DialogTitle className="font-heading text-xl flex items-center gap-2"><Rocket className="h-5 w-5 text-emerald-600" /> تفعيل حساب الشركة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">سيتم إنشاء حساب مدير + اشتراك للشركة <b>{actLead?.company_name}</b>.</p>
            <div className="space-y-1"><Label>جوال دخول المدير</Label><Input dir="ltr" value={actPhone} onChange={(e) => setActPhone(e.target.value)} data-testid="act-phone" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>الباقة</Label>
                <Select value={actPlan} onValueChange={setActPlan}><SelectTrigger data-testid="act-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>{PLAN_OPTIONS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent></Select></div>
              <div className="space-y-1"><Label>المدة</Label>
                <Select value={actMonths} onValueChange={setActMonths}><SelectTrigger data-testid="act-months"><SelectValue /></SelectTrigger>
                  <SelectContent>{[["1", "شهر"], ["3", "3 أشهر"], ["6", "6 أشهر"], ["12", "سنة"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            </div>
          </div>
          <DialogFooter><Button disabled={actBusy} onClick={doActivate} data-testid="act-confirm" className="bg-emerald-600 hover:bg-emerald-700 gap-1.5">{actBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} تفعيل</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!cred} onOpenChange={(o) => !o && setCred(null)}>
        <DialogContent dir="rtl" className="font-body sm:max-w-md" data-testid="activation-cred">
          <DialogHeader className="text-start"><DialogTitle className="font-heading text-xl text-emerald-700">✅ تم تفعيل الاشتراك</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p>تم إنشاء حساب لشركة <b>{cred?.company}</b>. سلّم بيانات الدخول للعميل (تُعرض مرة واحدة):</p>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1.5" dir="ltr">
              <div>📱 <b>{cred?.login_phone}</b></div>
              <div>🔑 <b className="font-mono">{cred?.temp_password}</b></div>
              <div>📦 {cred?.plan}</div>
            </div>
            <p className="text-xs text-slate-400">يدخل العميل من صفحة «تسجيل الدخول» بالجوال وكلمة المرور، ويُنصح بتغييرها.</p>
          </div>
          <DialogFooter><Button onClick={() => setCred(null)} className="bg-emerald-600 hover:bg-emerald-700">تم</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
